import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AlertController, Platform } from '@ionic/angular';
import { startWith, Subscription, take } from 'rxjs';
import { DeviceTypes } from 'src/packages/mitsBasics';
import { ErrorService } from 'src/packages/mitsBasics';
import { EventsService } from 'src/packages/mitsBasics';
import { FormatHelper } from 'src/packages/mitsBasics';
import { IPagy } from 'src/packages/mitsBasics';
import {
  IMitsFilter,
  IMitsSortable,
} from './../mits-filter-button/mits-filter-button.component';
import { MitsTableHelper } from './helpers/mits-table-helper';
import { PaginateHelper } from './helpers/paginate-helper';
import { IClickedItemEvent } from './interfaces/events/clicked-item-event';
import { IMitsTable } from './interfaces/mits-table';
import { MitsTableButton } from './interfaces/mits-table-button';
import { GetValuePipe } from './pipes/get-value';

@Component({
  selector: 'mits-table',
  templateUrl: './mits-table.component.html',
  styleUrls: ['./mits-table.component.scss'],
})
export class MitsTableComponent implements OnInit, AfterViewInit {
  /** Settings for the MitsTable */
  @Input() table: IMitsTable<any>;
  /** automatically unshift the remote id sortable to the sortable array */
  @Input() useRemoteId: boolean = true;
  /** Emits if the "+"-Button gets clicked */
  @Output() clickedAdd: EventEmitter<Event> = new EventEmitter<Event>();
  /** Emits if an item inside the table gets clicked */
  @Output() clickedItem: EventEmitter<IClickedItemEvent> =
    new EventEmitter<IClickedItemEvent>();
  /** After some data gets updated by an nested destroy this event will emit with all data including the modified item */
  @Output() afterNestedDestroy: EventEmitter<any[]> = new EventEmitter<any[]>();
  /** Data coming from the provider / data to use if no provider */
  private _data: Array<any>;

  public get data(): Array<any> {
    return this._data;
  }

  @Input()
  public set data(v: Array<any>) {
    if (this._data !== v) {
      this._data = v;
    }
  }
  /** Template for certain button to keep between filter and table */
  @Input() buttonTemplate!: TemplateRef<any>
  @Output() dataChange: EventEmitter<any[]> = new EventEmitter<any[]>();
  /** Pagination information coming from the provider */
  pageInfo: IPagy;
  /** Search Input Text */
  searchText = '';
  /** Buttons to show in actionmenu */
  actionMenuButtons: MitsTableButton<any>[];
  /** Fields wich should be sortable */
  sortableData: IMitsSortable[] = [];
  /** Sorting Arg */
  sortArg: string;
  /** HTML TableWrapper Element */
  @ViewChild('mitsTableWrapper')
  mitsTableWrapperElement: ElementRef<HTMLSpanElement>;
  /** HTML Input Element für Suche */
  @ViewChild('searchInput')
  searchInputElement: ElementRef<HTMLInputElement>;
  /** HTML Input Element for Page */
  @ViewChild('pageInput')
  pageInputElement: ElementRef<HTMLInputElement>;
  /** Debounced Search Timeout */
  debouncedSearchTimeout: any;
  /** Debounced Page Input Timeout */
  debouncedPageInputTimeout: any;
  /** FLAG: Is the screen small enough to show the icons in the menu and no longer the text? */
  showLeftMenuIcons: boolean;
  /** FLAG: Is the search-bar opened? */
  searchBarOpen = false;
  /** FLAG: Is the screen small enough to show the action-menu and no longer the buttons? */
  showActionMenu: boolean;
  /** FLAG: Is the screen so small that the contentWrap should be forced? */
  forceDisableContentWrap = false;
  /** FLAG: Has this Table started with data and so is a non provider Table? */
  isUnboundTable = false;
  /** FLAG: Table width */
  tableWidth: number;
  /** Var for different device types */
  deviceTypes: DeviceTypes;
  /** Forces the searchbar to stay open */
  protected forceOpenSearchBar: boolean;
  /** Default sortable found in the columns */
  protected defaultSortable: any;
  /** Vom Nutzer gewählte Gruppen-Werte (bei groupBySelect); leer = keine Gruppierung */
  protected selectedGroupValues: string[] = [];
  /** Auswählbare Gruppen-Werte (aus groupBySelect.provider geladen) */
  protected groupSelectValues: string[] = [];
  /** subscription for reloading data, for unsubscripe after destroy */
  private routeSubscription: Subscription;
  /** Alternative zum neuladen der Elemente. Wird benötigt für Offline */
  private reloadSubscription: Subscription;
  // FormGroup für die Globale Filterung nach bestimmten Attributen
  protected globalFilters: FormGroup = this.fb.group({});
  // Verfügbare Filter-Werte für die globale Filterung der Tabelle nach bestimmten Attributen
  protected globalFilterValues: Record<string, string[]> = {};
  // Flag, ob die globale Filterung eingeblendet ist
  protected globalFiltersVisible: boolean = false;

  constructor(
    public readonly getValuePipe: GetValuePipe,
    public readonly mitsTableHelper: MitsTableHelper,
    private readonly alertCtrl: AlertController,
    private readonly device: Platform,
    private readonly errorService: ErrorService,
    private readonly paginateHelper: PaginateHelper,
    private readonly route: ActivatedRoute,
    private readonly events: EventsService,
    private readonly fb: NonNullableFormBuilder,
  ) { }

  ngOnInit() {
    this.yourDevice();
    if (this.table) {
      this.forceOpenSearchBar = !this.table.header || this.table.header === '';
      this.defaultSortable = this.table.columns.find(c => c.defaultSortable);
      // Optionale initiale Sortierung ans Backend senden (vor dem ersten loadData).
      if (this.table.defaultOrderBy) this.sortArg = this.table.defaultOrderBy;
      // Auswählbare Gruppen-Werte laden (für den Gruppierungs-Auswahl-Button).
      this.table.groupBySelect?.provider().pipe(take(1))
        .subscribe(values => (this.groupSelectValues = values || []));
      if (!this.data) {
        if (this.table.provider) {
          this.loadData();
        }
      } else {
        this.isUnboundTable = true;
      }
      this.updateButtonPopover();
      this.setSortables();
    }

    if (!this.table.disableReload) {
      this.reloadSubscription = this.events
        .subscribe('reloadData')
        .subscribe(data => {
          setTimeout(() => this.loadData(), 500);
        });
    }
    // Globale Filterung nach bestimmten Attribut initialisieren
    this.initGlobalFilter();
  }

  /**
   * Reads out which device is used. Sets a flag to true for the respective device.
   */
  yourDevice() {
    this.deviceTypes = {
      desktop: this.device.is('desktop'),
      ios: this.device.is('ios'),
      android: this.device.is('android'),
    };
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.refreshSizeFlags(
      this.mitsTableWrapperElement.nativeElement.clientWidth,
    );
  }

  ngAfterViewInit(): void {
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      if (params.reloadMitsTable) {
        this.loadData();
      }
    });

    setTimeout(() => {
      this.refreshSizeFlags(
        this.mitsTableWrapperElement.nativeElement.clientWidth,
      );
    }, 50);
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();

    if (!this.table.disableReload) {
      this.reloadSubscription.unsubscribe();
    }
  }

  /**
   * Gets triggert if the user clicks on the search-button
   * @param event click-event
   */
  clickSearchButton(event: Event): void {
    if (
      this.table.disableSearch ||
      this.table.hideSearch ||
      this.forceOpenSearchBar
    ) {
      return;
    }
    this.searchBarOpen = !this.searchBarOpen;
    if (this.searchBarOpen) {
      this.searchInputElement.nativeElement.focus();
    } else {
      this.searchText = '';
      this.loadData();
    }
  }

  /**
   * Runs the search after some defined time any new call will cancel the old one
   */
  debounceSearch(ev: any) {
    if (this.isUnboundTable) {
      return;
    }
    if (this.debouncedSearchTimeout) {
      clearTimeout(this.debouncedSearchTimeout);
    }
    this.debouncedSearchTimeout = setTimeout(
      () => {
        this.paginateHelper.setPage(this.pageInfo, 1);
        this.loadData();
      },
      this.table.searchDebounceTime > 0 ? this.table.searchDebounceTime : 400,
    );
  }

  /**
   * Gets triggert if the user changes the filters on the filter-button
   * @param event click-event
   */
  changedFilters(event: IMitsFilter): void {
    if (
      this.table.disableFilters ||
      this.table.hideFilters ||
      this.table.applyFiltersAfterClose
    ) {
      return;
    }
    this.applyFilters(event);
  }

  /**
   * Gets triggert if the user closes the filter-button-popover
   * @param event click-event
   */
  closedFilters(event: IMitsFilter): void {
    if (
      this.table.disableFilters ||
      this.table.hideFilters ||
      !this.table.applyFiltersAfterClose
    ) {
      return;
    }
    this.applyFilters(event);
  }

  /**
   * Gets triggert if the user clicks on the add-button
   * @param event click-event
   */
  clickAddButton(event: Event): void {
    if (this.table.disableAdd || this.table.hideAdd) {
      return;
    }
    this.clickedAdd.emit(event);
  }

  /**
   * Blendet die Globalen Filter ein bzw. aus
   */
  toggleFilterVisability() {
    this.globalFiltersVisible = !this.globalFiltersVisible;
  }

  /**
   * Gets triggert if the user clicks on the individual item/row
   * @param event click-event
   * @param id ID of the clicked item
   */
  clickItem(event: Event, object: any, index: number): void {
    if (this.table.disableItemClick || this.shouldDisable(object)) {
      return;
    }
    this.clickedItem.emit({
      index,
      object,
      event,
    });
  }

  /**
   * Gets triggert if the user clicks on the delete button of an item/row
   * @param event click-event
   * @param index Index of the clicked item
   */
  async clickDeleteButton(event: Event, index: number) {
    const canDelete =
      !this.shouldDisable(this.data[index]) &&
      this.table.enableInlineEditing?.delete;
    if (canDelete) {
      const alert = await this.alertCtrl.create({
        header: $localize`Löschen`,
        message: $localize`Möchten Sie diesen Eintrag wirklich <strong>löschen</strong>?`,
        buttons: [
          {
            text: $localize`Abbrechen`,
            role: 'cancel',
          },
          {
            text: $localize`Löschen`,
            id: 'confirm-button',
            handler: () => {
              this.deleteItem(index);
            },
          },
        ],
      });
      await alert.present();
    } else {
      const alert = await this.alertCtrl.create({
        header: $localize`Löschen`,
        message: $localize`Dieser Eintrag kann nicht gelöscht werden!`,
        buttons: [
          {
            text: $localize`Zurück`,
            role: 'cancel',
          },
        ],
      });
      await alert.present();
    }
  }

  /**
   * Gets triggert if the user clicks on the first-page button
   * @param event click-event
   */
  clickFirstPageButton(event: Event): void {
    this.pageInfo.page = 1;
    this.loadData();
  }

  /**
   * Gets triggert if the user clicks on the previous-page button
   * @param event click-event
   */
  clickPrevPageButton(event: Event): void {
    this.pageInfo = this.paginateHelper.prevPage(this.pageInfo);
    this.loadData();
  }

  /**
   * Gets triggert if the user clicks on the next-page button
   * @param event click-event
   */
  clickNextPageButton(event: Event): void {
    this.pageInfo = this.paginateHelper.nextPage(this.pageInfo);
    this.loadData();
  }

  /**
   * Gets triggert if the user clicks on the last-page button
   * @param event click-event
   */
  clickLastPageButton(event: Event): void {
    this.pageInfo.page = this.pageInfo.last;
    this.loadData();
  }

  /**
   * Gets triggert if the user clicks on one specific page button
   * @param event click-event
   * @param page clicked page
   */
  inputSpecificPage(event: any): void {
    if (this.debouncedPageInputTimeout) {
      clearTimeout(this.debouncedPageInputTimeout);
    }
    this.debouncedPageInputTimeout = setTimeout(
      () => {
        this.pageInfo = this.paginateHelper.setPage(
          this.pageInfo,
          this.pageInputElement
            ? Number(this.pageInputElement.nativeElement.value)
            : this.pageInfo.page,
        );
        this.loadData();
      },
      this.table.searchDebounceTime > 0 ? this.table.searchDebounceTime : 400,
    );
  }

  /**
   * Generiert einen Link als String für das href in einem HTML-Element um direkt anrufen zu können
   * @param phonenumber Telefonnummer
   * @returns tel:+49xxxxxxxx || tel:xxxxxxxx
   */
  generatePhoneHref(phonenumber: any): string {
    return FormatHelper.generatePhoneHref(phonenumber);
  }

  /**
   * Loads the data with the given table-provider
   */
  loadData(): void {
    if (this.table.provider) {
      const params = this.table.params || {};
      params.page = this.pageInfo?.page || 1;
      params.search = btoa(this.searchText) || '';
      this.sortArg
        ? (params.order_by = this.sortArg)
        : this.useRemoteId
        ? (params.order_by = 'remote_id DESC')
        : (params.order_by = 'id DESC');
      // Globale Filterung nach bestimmten Attribut setzen
      if (this.table.globalFilter) this.applyGlobalFilterValueToParams(params);
      // Gruppierungs-Auswahl: gewählte Werte als Filter ans Backend senden.
      if (this.table.groupBySelect && this.selectedGroupValues.length) {
        params[this.table.groupBySelect.filterParam] = btoa(this.selectedGroupValues.join(','));
      }
      // Online bzw. Offline Request
      const where = this.table.forceRest
        ? this.table.provider.whereRemote(params)
        : this.table.provider.where(params);
      where.subscribe(res => {
        this.data = res.data;
        this.pageInfo = res.pagy;
      });
    }
  }

  clickedButton(
    button: MitsTableButton<any>,
    ev: Event,
    dataItem: any,
    i: number,
  ) {
    if (
      button.disabled ||
      (button.disabledFunc && button.disabledFunc(dataItem))
    )
      return;
    ev.stopPropagation();
    button.handler({
      event: ev,
      index: i,
      indexOptionalData: i,
      itemOptionalData: dataItem,
    });
  }

  /**
   * Returns true if the given dataItem should be highlighted
   * @param dataItem to check
   * @returns if the dataItem should be highlighted
   */
  shouldHighlight(dataItem: any): boolean {
    return this.table.highlightIfTrue?.names.some(name => {
      return !!this.getValuePipe.transform(dataItem, name);
    });
  }

  /**
   * Returns true if the given dataItem should be disabled
   * @param dataItem to check
   * @returns if the dataItem should be disabled
   */
  shouldDisable(dataItem: any): boolean {
    if (!this.table.disableIf?.name) return false;
    const v = this.getValuePipe.transform(dataItem, this.table.disableIf?.name);
    return this.table.disableIf?.values.includes(v);
  }

  /**
   * Feld, nach dem aktuell gruppiert wird – null, wenn keine Gruppierung.
   * Wird im Template an den groupBy-Pipe übergeben.
   */
  get activeGroupBy(): string {
    // Auswahl-Modus: nur gruppieren, wenn Werte gewählt sind; sonst dauerhaftes groupBy.
    return this.table.groupBySelect
      ? (this.selectedGroupValues.length ? this.table.groupBy : null)
      : (this.table.groupBy || null);
  }

  /**
   * Öffnet die Einzelauswahl der Gruppen-Werte. Nach 'Übernehmen' zeigt die
   * Liste nur die gewählte Gruppe (serverseitig gefiltert), gruppiert.
   * "Alle anzeigen" hebt die Gruppierung/Filterung wieder auf.
   */
  async openGroupingSelection(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: $localize`Gruppierung`,
      inputs: [
        {
          type: 'radio' as const,
          label: $localize`Alle anzeigen`,
          value: '',
          checked: this.selectedGroupValues.length === 0,
        },
        ...this.groupSelectValues.map(value => ({
          type: 'radio' as const,
          label: value,
          value,
          checked: this.selectedGroupValues[0] === value,
        })),
      ],
      buttons: [
        { text: $localize`Abbrechen`, role: 'cancel' },
        { text: $localize`Übernehmen`, handler: (value: string) => this.selectGrouping(value ? [value] : []) },
      ],
    });
    await alert.present();
  }

  /**
   * Setzt die gewählten Gruppen-Werte und lädt die Daten neu (Filter + Gruppierung).
   */
  selectGrouping(values: string[]): void {
    this.selectedGroupValues = values || [];
    this.loadData();
  }

  /**
   * Applies new filters to the data array
   * @param filters to apply
   */
  private applyFilters(filters: IMitsFilter) {
    this.sortArg =
      filters.field && filters.direction
        ? filters.field + ' ' + filters.direction.toUpperCase()
        : null;
    this.loadData();
  }

  /**
   * Refreshes all flags wich are size relevant
   * @param width width of the table in pixels
   */
  private refreshSizeFlags(width: number): void {
    this.tableWidth = width;
    this.showLeftMenuIcons = this.mitsTableHelper.isPixelsInThreshold(
      width,
      this.table.addTextThreshold || 'md',
    );
    this.showActionMenu =
      this.table.actionsMenuThreshold === 'none'
        ? false
        : this.mitsTableHelper.isPixelsInThreshold(
          width,
          this.table.actionsMenuThreshold || 'sm'
        );
    this.forceDisableContentWrap = this.mitsTableHelper.isPixelsInThreshold(
      width,
      this.table.disableContentWrapThreshold || 'md',
    );
  }

  /**
   * Deletes the item in the data array
   * @param index position in array
   */
  private deleteItem(index: number) {
    if (this.table.enableInlineEditing.delete.useNestedDestroy) {
      this.data[index]._destroy = true;
      this.afterNestedDestroy.emit(this.data);
    } else {
      if (this.table.provider) {
        const deleteObject = this.table.forceRest
          ? this.table.provider.deleteRemote(this.data[index].id)
          : this.table.provider.delete(this.data[index].id);
        deleteObject.subscribe(
          () => {
            this.data.splice(index, 1);
          },
          err => {
            this.errorService.handle(err);
          },
        );
      } else {
        this.data.splice(index, 1);
      }
    }
  }

  /**
   * Updates the list of buttons for the popover
   */
  private updateButtonPopover() {
    this.actionMenuButtons = [];
    if (this.table.actionsMenuButtons?.length > 0) {
      this.actionMenuButtons = this.actionMenuButtons.concat(
        this.table.actionsMenuButtons,
      );
    }
    if (this.table.enableInlineEditing) {
      if (this.table.enableInlineEditing.delete) {
        this.actionMenuButtons.push({
          icon: 'trash',
          color: '#eb445a',
          handler: event => {
            this.clickDeleteButton(event.event, event.index);
          },
        });
      }
    }
  }

  /**
   * Generates the List for sortables
   */
  private setSortables(): void {
    this.sortableData = this.table.columns
      .filter(
        tc =>
          tc.backend_attribute !== 'remote_id' &&
          tc.name !== 'remote_id' &&
          !tc.hideAsSortable,
      )
      .map(tc => {
        return {
          name: tc.backend_attribute ? tc.backend_attribute : tc.name,
          title: tc.title.default,
        };
      });
    if (this.useRemoteId)
      this.sortableData.unshift({
        name: 'remote_id',
        title: 'ID',
      });
  }

  /**
   * Initialisiert die globale Filterung der Tabelle nach bestimmten Attribut
   * - Lädt die möglichen Filter-Werte für das Attribut
   * - Reagiert auf Änderungen des durch den Nutzer gesetzten Filter-Wertes und lädt die Daten der Tabelle neu
   * @private
   */
  private initGlobalFilter(): void {
    if (
      this.table.globalFilter &&
      // Nutzung der Filter erfordert einen Online-Provider
      this.table.provider &&
      this.table.forceRest
    ) {
      try {
        this.table.globalFilter.filters.forEach((filter) => {
          const initialValue = filter.multiple ? [] : '';
          const control = this.fb.control(initialValue);
          this.globalFilters.addControl(filter.attribute as string, control);
          // Lade die möglichen Filterwerte für das Attribut
          this.table.globalFilter
            ?.filterValuesProvider(filter.attribute)
            .pipe(take(1))
            .subscribe(
              values => (this.globalFilterValues[filter.attribute] = values),
            );
          // Auf Änderungen des durch den Nutzer gesetzten Filter-Wertes reagieren und die Daten der Tabelle neu laden
          control.valueChanges
            .pipe(startWith(initialValue))
            .subscribe(() => this.loadData());
        });
      } catch (error) {
        console.error(
          '[MitsTableComponent][initGlobalFilter()]: Globaler Filter konnte nicht initialisert werden',
          error,
          this.table.globalFilter,
        );
      }
    }
  }

  /**
   * Setzt die Werte der globalen Filter in die übergebenen Parameter
   * @param params - Parameter, in denen die Werte der globalen Filter gesetzt werden sollen
   * @private
   */
  private applyGlobalFilterValueToParams(params: any): void {
    if (
      this.table.globalFilter &&
      // Nutzung der Filter erfordert einen Online-Provider
      this.table.provider &&
      this.table.forceRest
    ) {
      try {
        // Durchlaufe alle Controls in der FormGroup und setze die jeweiligen Werte in den Params
        Object.keys(this.globalFilters.controls).forEach(attribute => {
          const value = this.globalFilters.get(attribute)?.value;
          if (Array.isArray(value) && value.length > 0)
            params[attribute] = btoa(value.join(','));
          else params[attribute] = btoa(String(value));
        });
      } catch (error) {
        console.error(
          '[MitsTableComponent][applyGlobalFilterValueToParams]: Globale Filter konnten nicht gesetzt werden',
          error,
          this.table.globalFilter,
        );
      }
    }
  }
}
